import { X } from 'lucide-react';
import { AlertDialog, Dialog as D, DropdownMenu as M, Popover as P } from 'radix-ui';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { useUi } from '../lib/ui-provider';
import { Button } from './button';

const overlay = 'bl-anim-overlay fixed inset-0 bg-(--bl-overlay)';

/**
 * Overlays here are opened from state (menus, rows), not from a Radix Trigger, so Radix cannot
 * know where focus came from. Remember it when opening and restore it on close (WCAG 2.4.3).
 */
function useReturnFocus(open: boolean) {
  const origin = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (
      open &&
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body
    ) {
      origin.current = document.activeElement;
    }
  }, [open]);
  return (event: Event) => {
    const target = origin.current;
    if (target?.isConnected) {
      event.preventDefault();
      target.focus();
    }
  };
}

/** Modal dialog: focus is trapped, returned on close, Escape closes. */
export function Dialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { labels } = useUi();
  const returnFocus = useReturnFocus(props.open);
  return (
    <D.Root open={props.open} onOpenChange={props.onOpenChange}>
      <D.Portal>
        <D.Overlay className={cn(overlay, 'z-(--z-dialog)')} />
        <D.Content
          onCloseAutoFocus={returnFocus}
          className={cn(
            'bl-anim-dialog fixed start-1/2 top-[12vh] z-(--z-dialog) flex max-h-[76vh] w-[calc(100vw-2rem)] -translate-x-1/2 flex-col',
            'rounded-xl border border-line bg-surface shadow-overlay rtl:translate-x-1/2',
            props.size === 'lg' ? 'max-w-2xl' : props.size === 'sm' ? 'max-w-sm' : 'max-w-lg',
          )}
          {...(props.description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
            <div>
              <D.Title className="text-lg font-semibold text-ink">{props.title}</D.Title>
              {props.description ? (
                <D.Description className="mt-1 text-sm text-ink-muted">
                  {props.description}
                </D.Description>
              ) : null}
            </div>
            <D.Close
              aria-label={labels.close}
              className="-me-2 inline-flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted"
            >
              <X className="size-4" aria-hidden="true" />
            </D.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-3">{props.children}</div>
          {props.footer ? (
            <div className="flex flex-wrap justify-end gap-2 border-t border-line px-6 py-4">
              {props.footer}
            </div>
          ) : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/**
 * Confirmation for consequential actions. Cancel has initial focus; the confirm handler may be
 * async (the button shows progress and the dialog stays open on failure).
 */
export function ConfirmDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  onConfirm: () => Promise<unknown> | unknown;
  children?: ReactNode;
}) {
  const { labels } = useUi();
  const [busy, setBusy] = useState(false);
  const returnFocus = useReturnFocus(props.open);
  return (
    <AlertDialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={cn(overlay, 'z-(--z-dialog)')} />
        <AlertDialog.Content
          onCloseAutoFocus={returnFocus}
          className="bl-anim-dialog fixed start-1/2 top-[20vh] z-(--z-dialog) w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 rounded-xl border border-line bg-surface p-6 shadow-overlay rtl:translate-x-1/2"
        >
          <AlertDialog.Title className="text-lg font-semibold text-ink">
            {props.title}
          </AlertDialog.Title>
          <AlertDialog.Description className="mt-2 text-sm text-ink-muted">
            {props.description}
          </AlertDialog.Description>
          {props.children ? <div className="mt-4">{props.children}</div> : null}
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <AlertDialog.Cancel asChild>
              <Button variant="secondary">{labels.cancel}</Button>
            </AlertDialog.Cancel>
            <Button
              variant={props.tone === 'primary' ? 'primary' : 'danger'}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await props.onConfirm();
                  props.onOpenChange(false);
                } catch {
                  // The caller reports the error (toast/inline); keep the dialog open.
                } finally {
                  setBusy(false);
                }
              }}
            >
              {props.confirmLabel}
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/** Side panel. `side` is logical: `end` is the right edge in LTR and the left edge in RTL. */
export function Drawer(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  side?: 'start' | 'end';
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Hide the title visually (still announced), e.g. for navigation drawers. */
  hideTitle?: boolean;
}) {
  const { labels } = useUi();
  const side = props.side ?? 'end';
  const returnFocus = useReturnFocus(props.open);
  return (
    <D.Root open={props.open} onOpenChange={props.onOpenChange}>
      <D.Portal>
        <D.Overlay className={cn(overlay, 'z-(--z-drawer)')} />
        <D.Content
          onCloseAutoFocus={returnFocus}
          className={cn(
            'fixed inset-y-0 z-(--z-drawer) flex w-full flex-col bg-surface shadow-overlay',
            props.size === 'sm' ? 'max-w-72' : props.size === 'lg' ? 'max-w-lg' : 'max-w-md',
            side === 'end' ? 'bl-anim-drawer-end end-0' : 'bl-anim-drawer-start start-0',
            props.className,
          )}
          {...(props.description ? {} : { 'aria-describedby': undefined })}
        >
          <div
            className={cn(
              'flex items-start justify-between gap-4 border-b border-line px-5 py-4',
              props.hideTitle && 'sr-only',
            )}
          >
            <div>
              <D.Title className="text-lg font-semibold text-ink">{props.title}</D.Title>
              {props.description ? (
                <D.Description className="mt-1 text-sm text-ink-muted">
                  {props.description}
                </D.Description>
              ) : null}
            </div>
            <D.Close
              aria-label={labels.close}
              className="-me-2 inline-flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted"
            >
              <X className="size-4" aria-hidden="true" />
            </D.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{props.children}</div>
          {props.footer ? (
            <div className="flex justify-end gap-2 border-t border-line px-5 py-4">
              {props.footer}
            </div>
          ) : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

const menuSurface =
  'bl-anim-pop z-(--z-dropdown) min-w-52 rounded-lg border border-line bg-surface p-1 shadow-overlay';

export function Popover(props: {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'end';
  label?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}) {
  return (
    <P.Root
      {...(props.open === undefined ? {} : { open: props.open })}
      onOpenChange={props.onOpenChange}
    >
      <P.Trigger asChild>{props.trigger}</P.Trigger>
      <P.Portal>
        <P.Content
          align={props.align ?? 'end'}
          sideOffset={8}
          aria-label={props.label}
          className={cn(menuSurface, 'p-0', props.className)}
        >
          {props.children}
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}

export interface MenuItem {
  readonly id: string;
  readonly label: ReactNode;
  readonly icon?: ReactNode;
  readonly onSelect?: () => void;
  readonly tone?: 'danger';
  readonly disabled?: boolean;
  /** Renders the item as a link-like child (e.g. a router link) instead of a button. */
  readonly render?: (content: ReactNode) => ReactNode;
}

export interface MenuGroup {
  readonly id: string;
  readonly label?: ReactNode;
  readonly items: readonly MenuItem[];
}

/** Keyboard-navigable action menu (arrow keys, typeahead, Escape). */
export function DropdownMenu(props: {
  trigger: ReactNode;
  groups: readonly MenuGroup[];
  align?: 'start' | 'end';
  header?: ReactNode;
  label?: string;
}) {
  return (
    <M.Root>
      <M.Trigger asChild>{props.trigger}</M.Trigger>
      <M.Portal>
        <M.Content
          align={props.align ?? 'end'}
          sideOffset={8}
          className={menuSurface}
          aria-label={props.label}
        >
          {props.header ? <div className="px-3 py-2">{props.header}</div> : null}
          {props.groups.map((group, index) => (
            <M.Group key={group.id}>
              {index > 0 || props.header ? <M.Separator className="my-1 h-px bg-line" /> : null}
              {group.label ? (
                <M.Label className="px-3 py-1.5 text-xs font-medium text-ink-subtle">
                  {group.label}
                </M.Label>
              ) : null}
              {group.items.map((item) => {
                const content = (
                  <>
                    {item.icon ? (
                      <span className="shrink-0 text-ink-subtle" aria-hidden="true">
                        {item.icon}
                      </span>
                    ) : null}
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  </>
                );
                return (
                  <M.Item
                    key={item.id}
                    disabled={item.disabled}
                    onSelect={item.onSelect}
                    asChild={Boolean(item.render)}
                    className={cn(
                      'flex cursor-default items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none',
                      'data-[highlighted]:bg-surface-muted data-[disabled]:opacity-50',
                      item.tone === 'danger' ? 'text-danger' : 'text-ink',
                    )}
                  >
                    {item.render ? item.render(content) : content}
                  </M.Item>
                );
              })}
            </M.Group>
          ))}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}
